import { useMutation as apolloUseMutation } from '@apollo/client'
import {
  BulkCreatePlanningSubmissionPeriodDocument,
  LaunchPlanningSubmissionPeriodDocument,
  PlanningPeriodDetailDocument,
  UpdatePlanningSubmissionPeriodDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { mapStructuredErrors, parseBulkActionErrors } from 'modules/planning/lib/parseErrors'

const SUBMISSION_PERIOD_FIELD_MAPPING: Record<string, string> = {
  start_date: 'startDate',
  end_date: 'endDate',
  planning_period_id: 'startDate',
  startDate: 'startDate',
  endDate: 'endDate',
}

interface UseSubmissionPeriodsParams {
  periodId: string
  companyId: string
  submissionPeriodId?: string
}

const useSubmissionPeriods = ({
  periodId,
  companyId,
  submissionPeriodId,
}: UseSubmissionPeriodsParams) => {
  const [launchMutation] = useMutation(LaunchPlanningSubmissionPeriodDocument, {
    extractorFn: (data) => data.planningMutations.launchPlanningSubmissionPeriod,
  })

  const [updateMutation] = useMutation(UpdatePlanningSubmissionPeriodDocument, {
    extractorFn: (data) => data.planningMutations.updatePlanningSubmissionPeriod,
  })

  const [bulkCreateMutation] = apolloUseMutation(BulkCreatePlanningSubmissionPeriodDocument)

  const bulkCreate = async (startDate: string, endDate: string) => {
    const { data: responseData } = await bulkCreateMutation({
      variables: {
        items: [
          {
            companyId,
            planningPeriodId: periodId,
            startDate,
            endDate,
          },
        ],
      },
      refetchQueries: [PlanningPeriodDetailDocument],
    })

    const payload = responseData?.planningMutations?.bulkCreatePlanningSubmissionPeriod
    const errors = payload?.errors ?? []

    if (errors.length > 0) {
      const fieldErrors = parseBulkActionErrors(errors, SUBMISSION_PERIOD_FIELD_MAPPING)
      return { success: false, errors: fieldErrors }
    }

    return { success: true, errors: {} }
  }

  const launch = async (endDate: string) => {
    const result = await launchMutation({
      variables: {
        companyId,
        planningPeriodId: periodId,
        submissionPeriodId,
        endDate,
      },
      refetchQueries: [PlanningPeriodDetailDocument],
    })

    let fieldErrors: Record<string, string> = {}
    const handled = handleResult(result, (errors) => {
      fieldErrors = mapStructuredErrors(errors, SUBMISSION_PERIOD_FIELD_MAPPING)
    })

    return { success: handled.success, errors: fieldErrors }
  }

  const update = async (startDate: string, endDate: string) => {
    if (!submissionPeriodId) {
      return { success: false, errors: {} }
    }

    const result = await updateMutation({
      variables: {
        id: submissionPeriodId,
        startDate,
        endDate,
      },
      refetchQueries: [PlanningPeriodDetailDocument],
    })

    let fieldErrors: Record<string, string> = {}
    const handled = handleResult(result, (errors) => {
      fieldErrors = mapStructuredErrors(errors, SUBMISSION_PERIOD_FIELD_MAPPING)
    })

    return { success: handled.success, errors: fieldErrors }
  }

  return { bulkCreate, launch, update }
}

export default useSubmissionPeriods
